/**
 * Pure utility functions for calculating friendship goal progress.
 * No React dependencies - these are pure JavaScript functions.
 */

/**
 * Calculate progress toward friendship goals
 * @param {Object} params - Parameters object
 * @param {Array} params.friends - Array of friend objects
 * @param {Object} params.friendshipNeeds - Friendship needs profile with preferences
 * @returns {Object|null} Progress object with closeFriendsCount and emotionalDepth, or null
 */
export const calculateProgress = ({ friends, friendshipNeeds }) => {
  if (!friendshipNeeds?.preferences) return null;

  const progress = {
    closeFriendsCount: null,
    emotionalDepth: null
  };

  // Calculate close friends progress
  const closeFriendTypes = ['close', 'lifelong', 'chosen-family'];
  // Count friends as "close" if their relationship type is in closeFriendTypes OR they have countsAsCloseFriend flag
  const closeFriends = friends.filter(friend => 
    (friend.relationship && closeFriendTypes.includes(friend.relationship)) || friend.countsAsCloseFriend === true
  );
  const targetCount = friendshipNeeds.preferences.closeFriendCount?.target;
  
  // Always calculate progress if target exists (even if 0)
  if (targetCount !== undefined) {
    progress.closeFriendsCount = {
      current: closeFriends.length,
      target: targetCount,
      percentage: Math.min(100, Math.round((closeFriends.length / targetCount) * 100)),
      remaining: Math.max(0, targetCount - closeFriends.length)
    };
  }

  // For emotional depth, always show progress if preference exists
  const desiredDepth = friendshipNeeds.preferences.emotionalDepth?.level;
  if (desiredDepth) {
    progress.emotionalDepth = {
      hasCloseFriends: closeFriends.length > 0,
      totalCloseFriends: closeFriends.length,
      desiredLevel: desiredDepth
    };
  }

  return progress;
};
